import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, ExternalLink, Send, CheckCheck, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { SignRequestDialog } from "@/components/SignRequestDialog";
import { api, apiError } from "@/lib/api";

const DOC_ROLES = ["admin", "manager", "agent"];
const ago = (v) => { const h = Math.floor((Date.now() - new Date(v).getTime()) / 3600000); return h >= 48 ? `hace ${Math.floor(h / 24)} días` : `hace ${h} h`; };

export const useSignAlertsCount = (user) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!DOC_ROLES.includes(user?.role)) return undefined;
    const load = () => api.get("/docs/alerts").then((r) => setN(r.data.length)).catch(() => {});
    load();
    const t = setInterval(load, 300000);
    window.addEventListener("sign-alerts-changed", load);
    return () => { clearInterval(t); window.removeEventListener("sign-alerts-changed", load); };
  }, [user?.role]);
  return n;
};

const ManualDialog = ({ alert, onClose, onDone }) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setNote(""); }, [alert]);
  const save = async () => {
    setBusy(true);
    try { await api.post(`/docs/files/${alert.id}/manual-signed`, { note }); toast.success("Firma registrada"); onDone(); onClose(); }
    catch (e) { toast.error(apiError(e).message); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!alert} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="manual-sign-dialog">
        <DialogHeader>
          <DialogTitle>¿Recibiste la firma por otro medio?</DialogTitle>
          <DialogDescription>Se marcará el certificado de {alert?.guest_name} como firmado y se cancelará el enlace de firma. Queda registrado en el historial del cobro.</DialogDescription>
        </DialogHeader>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder="¿Cómo la recibiste? Ej. firmó en papel en recepción, envió foto por WhatsApp…" data-testid="manual-sign-note" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="manual-sign-cancel">Cancelar</Button>
          <Button onClick={save} disabled={busy || note.trim().length < 3} data-testid="manual-sign-confirm">Confirmar firma</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const SignAlerts = ({ onOpen, onChanged }) => {
  const [list, setList] = useState([]);
  const [resend, setResend] = useState(null);
  const [manual, setManual] = useState(null);
  const load = useCallback(() => api.get("/docs/alerts").then((r) => setList(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const changed = () => { load(); onChanged?.(); window.dispatchEvent(new Event("sign-alerts-changed")); };
  const seen = async (a) => {
    try { await api.post(`/docs/files/${a.id}/alert-seen`); toast.success("Aviso ocultado"); changed(); } catch (e) { toast.error(apiError(e).message); }
  };
  if (!list.length) return null;
  return (
    <section className="rounded-2xl border border-rose-200 bg-rose-50/60 p-5" data-testid="sign-alerts">
      <h2 className="mb-1 flex items-center gap-2 font-heading text-base font-semibold text-rose-900"><BellRing size={18} />Firmas pendientes de más de 24 h ({list.length})</h2>
      <p className="mb-4 text-xs text-rose-800/80">Estos huéspedes recibieron su certificado o enlace de firma y aún no firman.</p>
      <div className="space-y-2">
        {list.map((a) => (
          <div key={a.id} className="flex flex-col gap-3 rounded-xl border border-rose-100 bg-white p-3 lg:flex-row lg:items-center" data-testid={`sign-alert-${a.charge_id}`}>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-slate-900">{a.guest_name || "Sin nombre"}</p>
              <p className="truncate text-xs text-slate-500">{[a.template_name, a.email].filter(Boolean).join(" · ")}</p>
              <p className="mt-1 inline-flex rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700" data-testid={`sign-alert-ago-${a.charge_id}`}>Enviado {ago(a.since)}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => onOpen(a.charge_id)} data-testid={`sign-alert-open-${a.charge_id}`}><ExternalLink size={14} className="mr-1" />Abrir venta</Button>
              <Button size="sm" onClick={() => setResend({ id: a.id, lang: a.lang })} data-testid={`sign-alert-resend-${a.charge_id}`}><Send size={14} className="mr-1" />Reenviar enlace</Button>
              <Button size="sm" variant="outline" className="border-emerald-200 text-emerald-700" onClick={() => setManual(a)} data-testid={`sign-alert-manual-${a.charge_id}`}><CheckCheck size={14} className="mr-1" />Firma recibida por otro medio</Button>
              <Button size="sm" variant="ghost" className="text-slate-500" onClick={() => seen(a)} data-testid={`sign-alert-seen-${a.charge_id}`}><EyeOff size={14} className="mr-1" />Marcar como visto</Button>
            </div>
          </div>
        ))}
      </div>
      <SignRequestDialog doc={resend} onOpenChange={(o) => { if (!o) { setResend(null); changed(); } }} />
      <ManualDialog alert={manual} onClose={() => setManual(null)} onDone={changed} />
    </section>
  );
};
